import * as React from "react";
import moment from "moment-timezone";

import { formatDate } from "src/library/utils";

export type DateTemplatesType = "long_date" | "count_down" | "time" | "datetime" | "timedate" | "long_date_time" | "time_since";

interface IProps {
  date: Date | string | null;
  format?: string;
  template?: DateTemplatesType;
  className?: string;
  hideCountdownEnd?: boolean;
  /** Used to make sure the date is not displayed in local time. Otherwise delivery date shows up wrong in some cases */
  ignoreLocal?: boolean;
}

const Datetime: React.ReactFCWithChildren<IProps> = ({
  date, format, template, hideCountdownEnd, ignoreLocal = false
}) => {
  const [ dateString, setDateString ] = React.useState("-");
  const memoDate = React.useMemo(() => date, [ date ]);

  // returns a formatted date string
  const getFormattedDateTime = React.useCallback(() => {
    const diffSeconds = moment.utc(memoDate).diff(moment.utc(), "seconds");
    const diffHours = moment.utc(memoDate).diff(moment.utc(), "hours");
    const diffMinutes = moment.utc(memoDate).diff(moment.utc(), "minutes");
    const diffDays = moment.utc(memoDate).diff(moment.utc(), "days");
    const diffMinutesAbsolute = Math.abs(diffMinutes);
    const diffHoursAbsolute = Math.abs(diffHours);
    const diffDaysAbsolute = Math.abs(diffDays);
    const isToday = moment.utc().isSame(moment.utc(memoDate), "day");

    if (memoDate) {
      let formatStr: string | undefined;

      switch (template) {
        case "long_date_time":
          formatStr = "HH:mm dddd D MMMM YYYY";
          break;

        case "long_date":
          formatStr = "ddd D MMM YYYY";
          break;

        case "time":
          formatStr = "HH:mm";
          break;

        case "datetime":
          formatStr = "DD/MM/YYYY - HH:mm";
          break;

        case "timedate":
          formatStr = "HH:mm - DD/MM/YYYY";
          break;

        case "count_down":
          if (diffSeconds < 0) {
            return setDateString("-");
          } else if (diffMinutes < 1) {
            return setDateString(`${diffSeconds} seconds`);
          } else if (diffHours < 1) {
            return setDateString(`${diffMinutes} minutes ${diffSeconds - (diffMinutes * 60)} seconds`);
          } else if (diffHours <= 12) {
            return setDateString(`${diffHours} hour${diffHours === 1 ? " " + (diffMinutes - 60) + " minutes" : "s"} ${!hideCountdownEnd ? `(${formatDate(memoDate, "HH:mm")})` : ""}`);
          } else if (diffHours < 24) {
            if (isToday) {
              formatStr = "HH:mm";
            } else {
              formatStr = "ddd HH:mm";
            }
          } else {
            formatStr = "ddd D MMM YYYY";
          }
          break;

        case "time_since":
          if (diffMinutesAbsolute < 60) {
            return setDateString(`${diffMinutesAbsolute} minute${diffMinutesAbsolute > 1 ? "s" : ""} ago`);
          } else if (diffHoursAbsolute < 24) {
            return setDateString(`${diffHoursAbsolute} hour${diffHoursAbsolute > 1 ? "s" : ""} ago`);
          } else if (diffDaysAbsolute < 30) {
            return setDateString(`${diffDaysAbsolute} day${diffDaysAbsolute > 1 ? "s" : ""} ago`);
          } else {
            formatStr = "ddd D MMM YYYY";
          }
          break;

        default:
          formatStr = format || "DD/MM/YYYY";
      }

      return setDateString(formatDate(memoDate, formatStr, ignoreLocal));
    }

    return setDateString("-");
  }, [
    memoDate,
    template,
    ignoreLocal,
    format,
    hideCountdownEnd
  ]);

  React.useEffect(() => {
    let interval: NodeJS.Timeout;

    // when counting down allow it to tick per second
    if (template === "count_down") {
      interval = setInterval(() => {
        getFormattedDateTime();
      }, 1000);
    } else {
      getFormattedDateTime();
    }

    return () => interval ? clearInterval(interval) : undefined;
  }, [ getFormattedDateTime, template ]);

  return (
    <>
      {dateString}
    </>
  );
};

export default Datetime;
